/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.20.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import { icons } from '@assets/index';
import { Button, LinkBlock } from '@components/common';
import { ImgStatusError } from '@components/common/Block/Img';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { getFormLocale } from '@modules/form/const/formLocalization';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import {
    makeSelectCreateInquiryTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import Modal from '@modules/modal/components/Modal';
import { ModalCenterStyled } from '@modules/modal/components/styled/ModalCenterStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import { stringFormatParams } from '@utils/stringFormat';

import {
    ModalSeparatorStyled,
    ModalTitleStyled,
    ModalTitleTextStyled,
} from '../../modal/components/styled/ModalStyled';
import {
    CreateInquiryInitialValues,
    ECreateInquiryField as F,
    ECreateInquiryCustomAttributeCode as A,
} from '../constants';
import { inquiryApi } from '../inquiryApi';
import { mapCustomAttributeValues } from '../mapping';
import type { ICreateInquiryChangeAttributeQuery, ICreateInquiryForm, TCreateInquiryFormValidation } from '../types';
import { TCreateInquiryAttributeValue } from '../types';
import CreateInquiryAdditionalAttributes from './Attributes/CreateInquiryAdditionalAttributes';
import CreateInquiryCustomAttributes from './Attributes/CreateInquiryCustomAttributes';
import CreateInquiryFileAttribute from './Attributes/CreateInquiryFileAttribute';
import CreateInquiryInitialAttributes from './Attributes/CreateInquiryInitialAttributes';
import type { TCreateInquiryModal } from './CreateInquiryModal';
import CreateInquiryModalSuccess from './CreateInquiryModalSuccess';
import { CreateInquiryModalCloseIconStyled } from './styled/CreateInquiryModalStyled';

export const CreateInquiryV3Modal: FC<TCreateInquiryModal> = ({ type, id, initialTopicCode, onCloseModal }) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { start: tInquiryStart, failure: tInquiryFailure } = useSelector(makeSelectCreateInquiryTranslation());
    const isServiceUserEnabled = useSelector(makeSelectAuthoritiesIsServiceUser());

    Yup.setLocale(getFormLocale(translationCommon.form.validation));

    const initialValidationScheme: TCreateInquiryFormValidation = useMemo(
        () => ({
            section: Yup.object().shape({ value: Yup.string().required() }),
            topic: Yup.object().shape({ value: Yup.string().required() }),
        }),
        [],
    );

    const [validationSchemeConf, setValidationSchemeConf] = useState(initialValidationScheme);
    const [validationScheme, setValidationScheme] = useState(Yup.object());

    const [initialValues, setInitialValues] = useState<ICreateInquiryForm>(CreateInquiryInitialValues);

    const dispatch = useDispatch();

    /* API */

    const [
        getSectionsAndTopics,
        {
            isUninitialized,
            isLoading: isSectionAndTopicLoading,
            isSuccess: isSectionsAndTopicsLoaded,
            data: sectionAndTopics,
        },
    ] = inquiryApi.endpoints.getSectionsAndTopics.useLazyQuery();

    const [getAttributes, { data: attributes, isLoading: isAttributesLoading }] =
        inquiryApi.endpoints.getAttributes.useLazyQuery();

    const [changeAttribute, { isLoading: isChangingAttribute }] = inquiryApi.endpoints.changeAttribute.useMutation();

    const [save, { data: saveResult, isLoading: isSaving, error: saveError }] = inquiryApi.endpoints.save.useMutation();

    /* Получение разделов и соответствующих тем  */
    useEffect(() => {
        getSectionsAndTopics({ type, id, initialTopicCode });
    }, [getSectionsAndTopics, type, id, initialTopicCode]);

    useEffect(() => {
        dispatch(
            logBusinessEvent({
                eventName: ELogBusinessEventName.B2B_SHOW_INQUIRY_MASTER,
                type: ELogBusinessEventType.common,
            }),
        );
    }, [dispatch]);

    /* Обновление валидации при получении\изменении custom || service атрибутов */
    useEffect(() => {
        setValidationScheme(Yup.object().shape(validationSchemeConf));
    }, [validationSchemeConf]);

    /* Получение всех дополнительных атрибутов */
    const onGetAttributes = useCallback(
        (topicCode: string, validateForm?: () => void) => {
            getAttributes({ topicCode, type, id }).then(() => {
                setTimeout(() => {
                    if (validateForm) {
                        validateForm();
                    }
                });
            });
        },
        [getAttributes, id, type],
    );

    useEffect(() => {
        if (isSectionsAndTopicsLoaded && sectionAndTopics?.initialValues) {
            const { section, topic } = sectionAndTopics.initialValues;

            setInitialValues({
                ...CreateInquiryInitialValues,
                section,
                topic,
            });

            onGetAttributes(String(topic.value));
        }
    }, [isSectionsAndTopicsLoaded, onGetAttributes, sectionAndTopics?.initialValues]);

    /* Изменение мастер атрибута и получение slave атрибутов с обновленной конфигурацией */
    const onChangeAttribute = (
        topicCode: string,
        attributeId: number,
        masterAttributes: ICreateInquiryChangeAttributeQuery['masterAttributes'],
        validateForm: () => void,
    ) => {
        changeAttribute({
            topicCode,
            type,
            id,
            attributeId,
            masterAttributes,
        }).then(() => {
            setTimeout(() => {
                validateForm();
            });
        });
    };

    const onValidationSchemeConfChanged = useCallback(
        (validation: TCreateInquiryFormValidation) => {
            setValidationSchemeConf({ ...initialValidationScheme, ...validation });
        },
        [initialValidationScheme],
    );

    const onSubmit = ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        section,
        topic,
        description,
        phone,
        email,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        file,
        ...customAttrsValues
    }: ICreateInquiryForm) => {
        // Logging
        dispatch(
            logBusinessEvent({
                eventName: ELogBusinessEventName.USER_CREATE_INQUIRY,
                type: ELogBusinessEventType.common,
            }),
        );
        GTMOperations.navigation.inquiryCreateClickOperation();

        // Map form custom attributes values to api configuration
        const customAttrsMapper = mapCustomAttributeValues(customAttrsValues);
        const saveAttributes = (attributes?.customAttrs?.map(customAttrsMapper) ?? []).filter(
            (item) => item !== null,
        ) as TCreateInquiryAttributeValue[];

        // API save
        save({
            attributes: saveAttributes,

            [F.topic]: { code: topic.value.toString() },
            // additional attributes
            [F.description]: description,
            [F.phone]: phone,
            [F.email]: email,
            type,
            id,
        })
            .unwrap()
            .then(() => {
                dispatch(
                    modalSetStatusAction({
                        status: EModalStatus.success,
                    }),
                );

                dispatch(
                    logBusinessEvent({
                        eventName: ELogBusinessEventName.B2B_CREATE_INQUIRY,
                        type: ELogBusinessEventType.common,
                    }),
                );

                GTMOperations.navigation.inquirySuccessfulCreateOperation();
            })
            .catch((e: unknown) => {
                dispatch(
                    modalSetStatusAction({
                        status: EModalStatus.failure,
                    }),
                );

                GTMOperations.navigation.inquiryErrorCreateOperation(JSON.stringify(e || {}));
            });
    };

    const onErrorBtnClick = () => {
        dispatch(
            modalSetStatusAction({
                status: EModalStatus.start,
            }),
        );
        onCloseModal();
    };

    const setCreateButton = (values: ICreateInquiryForm, isValid: boolean, dirty: boolean, isSubmitting: boolean) => {
        const acessRegistrationAttribute = attributes?.customAttrs.find(
            (attr) => attr.code === A.createMarkerAttributeCode,
        );

        const isFileRequired = !!attributes?.serviceAttributes.attachmentMandatory && !values.file.size;

        // Проверяем атрибут-маркет, разрешающий регистрацию обращения
        if (!!acessRegistrationAttribute && acessRegistrationAttribute.declaration.numberValue === 0) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.notAvailable,
                disabled: true,
            };
        }

        // Проверяем обязательные атрибуты
        if (!isValid || !dirty) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.notValid,
                disabled: true,
            };
        }

        // Проверяем наличие прикрепленного файла
        if (isFileRequired) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.fileRequired,
                disabled: true,
            };
        }

        return { tooltipTitle: '', disabled: isSubmitting };
    };

    return (
        <Modal dataName="create-inquiry-modal" style={{ padding: '24px' }} width="640px">
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.createInquiry]} />

            <Modal.Status status={EModalStatus.start}>
                <Modal.Title
                    className="modal-popup-title__base"
                    subTitle={stringFormatParams(tInquiryStart.subTitle[type], {
                        [ELocaleKeys.number]: sectionAndTopics?.userInfo?.name ?? '',
                        [ELocaleKeys.string]: sectionAndTopics?.userInfo?.label ?? '',
                    })}
                >
                    <ModalTitleStyled>
                        <ModalTitleTextStyled>{tInquiryStart.title}</ModalTitleTextStyled>
                        <CreateInquiryModalCloseIconStyled src={icons.cancelSmallIcon} onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <ModalSeparatorStyled margin="24px" />
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationScheme}
                    onSubmit={onSubmit}
                >
                    {({ values, submitForm, isValid, dirty, isSubmitting }) => {
                        const { tooltipTitle, disabled } = setCreateButton(values, isValid, dirty, isSubmitting);
                        return (
                            <Loader
                                showLoader={
                                    isUninitialized ||
                                    !tInquiryStart.title ||
                                    isSectionAndTopicLoading ||
                                    isAttributesLoading ||
                                    isChangingAttribute ||
                                    isSaving
                                }
                            >
                                <Modal.Body style={{ maxHeight: 'initial', paddingBottom: '24px', paddingTop: '24px' }}>
                                    <CreateInquiryInitialAttributes
                                        getAttributes={onGetAttributes}
                                        id={id}
                                        initialTopicCode={initialTopicCode}
                                        type={type}
                                    />

                                    {values[F.topic]?.value && (
                                        <CreateInquiryCustomAttributes
                                            attributes={attributes}
                                            id={id}
                                            setValidation={onValidationSchemeConfChanged}
                                            topicCode={values[F.topic]?.value as string}
                                            type={type}
                                            onChangeAttribute={onChangeAttribute}
                                        />
                                    )}

                                    {
                                        // Дополнительные поля
                                        values[F.topic]?.value && (
                                            <CreateInquiryAdditionalAttributes
                                                contactInfo={sectionAndTopics?.contactInfo}
                                                serviceAttributes={attributes?.serviceAttributes}
                                            />
                                        )
                                    }

                                    {
                                        // Прикрепить файл
                                        values[F.topic]?.value && !!attributes?.serviceAttributes.saveAvailable && (
                                            <CreateInquiryFileAttribute
                                                allowedFileExtensions={attributes?.allowedFileExtensions}
                                                maxFileCount={attributes?.maxFileCount}
                                                required={!!attributes?.serviceAttributes?.attachmentMandatory}
                                                type={type}
                                            />
                                        )
                                    }
                                    <div>
                                        <LabelMarkStyled>*</LabelMarkStyled>&nbsp;
                                        {translationCommon.form.requiredField}
                                    </div>
                                </Modal.Body>
                                <ModalSeparatorStyled margin="24px" />
                                <Modal.Control className="modal-popup-control__base">
                                    <ArrowTooltip disableHoverListener={isServiceUserEnabled} title={tooltipTitle}>
                                        <span>
                                            <ButtonServiceUserDenied
                                                disabled={disabled}
                                                kind="mediumNormal"
                                                tooltipMsg={
                                                    !isValid || !dirty ? tInquiryStart.createBtnValidation.notValid : ''
                                                }
                                                onClick={submitForm}
                                            >
                                                {tInquiryStart.createBtn}
                                            </ButtonServiceUserDenied>
                                        </span>
                                    </ArrowTooltip>
                                    <LinkBlock onClick={onCloseModal}>{translationCommon.modal.close}</LinkBlock>
                                </Modal.Control>
                            </Loader>
                        );
                    }}
                </Formik>
            </Modal.Status>

            <Modal.Status status={EModalStatus.success}>
                <CreateInquiryModalSuccess
                    fileAttachedWithErrors={saveResult?.fileAttachedWithErrors}
                    id={saveResult?.id}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>

            <Modal.Status status={EModalStatus.failure}>
                <ModalCenterStyled>
                    <ImgStatusError />
                    <Modal.Title>{tInquiryFailure.title}</Modal.Title>
                    <Modal.Text>
                        {saveError?.error?.code
                            ? translationCommon.error.codes[saveError?.error?.code]
                            : translationCommon.error.defaultMessage}
                    </Modal.Text>
                    <Modal.Control>
                        <Button onClick={onErrorBtnClick}>{translationCommon.modal.accessibly}</Button>
                    </Modal.Control>
                </ModalCenterStyled>
            </Modal.Status>
        </Modal>
    );
};
